/**
 * @module utils/columnSort/syncFromQB/syncDimensionPercentage
 * @description dimension_percentage 变更时同步 dim_pct 虚拟列（对齐后端契约）
 *
 * 后端契约要点（更新于 2026-06-25）：
 * - 每条 metrics[] 都有自己的 group_by；同一 (table_code, metric) 可配多条
 * - origin_name 按同 (table_code, metric) 子序列出现顺序追加 `_2`/`_3` 后缀：
 *     第 1 条 → `dim_pct_{column}`
 *     第 2 条 → `dim_pct_{column}_2`
 *     第 3 条 → `dim_pct_{column}_3`
 * - 列顺序：维度列 → 每个源指标：源指标 → 维度占比 dim_pct（若有）→ 列占比 % （若有）→ 对比时间列…
 *   dimension_percentage 展示名多为 `%指标名`（无空格）；column_percentage 为 `% 指标名`（PERCENTAGE_PREFIX 含空格）
 *   多源指标时维度占比紧跟各自源指标，不按 metrics[] 全局下标交错：
 *     配置 [revenue+country, orders+country, revenue+product]
 *     → revenue → dim%(country) → dim%(product) → orders → dim%(country)
 *
 * 流程：
 *   1. 清掉已不存在的 dim_pct_* 列（已配置的 metric 不在 metrics[] 中时移除）
 *   2. 按源指标分组 metrics[]：groupMetrics[table_code][metric] = metrics[] 子序列
 *   3. 遍历 column_sort，遇到一个源 metric 列时，紧跟其后插入该源指标的 dim_pct 子序列
 *   4. 已经被持久化的旧 dim_pct 列（序号 `_2`/`_3`）保留并按子序列顺序调整位置
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import type { IDimensionPercentageConfig } from '@dbl/pages/Multidimensional/Analysis/components/ChartSettingsV2/types/settings/dimensionPercentage';
import {
  isColumnSortDimension,
  isCompareUserFilterColumn,
} from '../untils/keys';
import { isPercentVirtualColumn } from '../untils/fieldUtils';
import {
  dimPctColumnNameAt,
  isDimPctVirtualColumn,
  parseDimPctColumn,
} from '../untils/dimensionPercentage';

export type { IDimensionPercentageConfig } from '@dbl/pages/Multidimensional/Analysis/components/ChartSettingsV2/types/settings/dimensionPercentage';

interface IMetricConfig {
  table_code: string;
  metric: string;
  label: string;
}

/** 收集配置里所有合法 metric（按配置顺序） */
function collectMetrics(
  config: IDimensionPercentageConfig | undefined,
): IMetricConfig[] {
  if (!config || typeof config !== 'object') return [];
  const metrics = Array.isArray(config.metrics) ? config.metrics : [];
  return metrics
    .filter(
      m =>
        !!m?.metric &&
        !!m?.label?.trim() &&
        Array.isArray(m.group_by) &&
        m.group_by.length >= 1,
    )
    .map(m => ({
      table_code: m.table_code || 't0',
      metric: m.metric,
      label: m.label || '',
    }));
}

/**
 * 已持久化 column_sort 时：移除无效 dim_pct 列，按 dimension_percentage 在主指标后插入。
 *
 * @param list - 已持久化的 column_sort
 * @param dimensionPercentage - 当前 dimension_percentage 配置
 * @returns 同步 dim_pct 虚拟列后的 column_sort
 */
export function applyDimensionPctVirtualColumns(
  list: IColumnSortItem[],
  dimensionPercentage: IDimensionPercentageConfig | undefined,
): IColumnSortItem[] {
  const metrics = collectMetrics(dimensionPercentage);

  // 按 (table_code, metric) 分组：得到每个源指标自己的 metrics[] 子序列（按配置顺序）
  const groupByMetric = new Map<string, IMetricConfig[]>();
  metrics.forEach(m => {
    const k = `${m.table_code}::${m.metric}`;
    if (!groupByMetric.has(k)) groupByMetric.set(k, []);
    groupByMetric.get(k)!.push(m);
  });

  // 记录每个源指标在配置中分配的 dim_pct 序号
  const assigned = new Set<string>(); // ${table_code}::${metric}::${index}
  metrics.forEach(m => {
    const key = `${m.table_code}::${m.metric}`;
    const seq = groupByMetric.get(key) || [];
    const idx = seq.findIndex(x => x === m) + 1; // 1-based
    assigned.add(`${m.table_code}::${m.metric}::${idx}`);
  });

  // 1) 清掉不再需要的 dim_pct 列；保留仍需要的（保留原有展示名/table_id 等）
  const filtered: IColumnSortItem[] = [];
  for (const item of list) {
    if (
      isCompareUserFilterColumn(item.column) ||
      item.is_compare_date ||
      isColumnSortDimension(item) ||
      isPercentVirtualColumn(item) ||
      !isDimPctVirtualColumn(item)
    ) {
      filtered.push(item);
      continue;
    }
    const tableCode = item.table_code || 't0';
    const parsed = parseDimPctColumn(item.column);
    if (!parsed) {
      filtered.push(item);
      continue;
    }
    const k = `${tableCode}::${parsed.metric}::${parsed.index}`;
    if (assigned.has(k)) {
      filtered.push(item);
    }
    // 否则：metric 已不配置 → drop
  }

  // 无合法 dimension_percentage 配置：仅做步骤 1 清理 orphan dim_pct，其余列原样返回
  if (metrics.length === 0) {
    return filtered;
  }

  // 2) 遍历：遇到主 metric 列时，紧跟其后插入该源指标的 dim_pct 子序列
  //    已存在的 dim_pct 列将被去重（按序号），然后按 metrics[] 顺序重排
  const next: IColumnSortItem[] = [];
  // 收集每个源指标下「被重新生成」的 dim_pct 项（按 metrics[] 顺序）
  const generatedByMetric = new Map<
    string,
    Array<{ item: IColumnSortItem; key: string; }>
  >();

  // 先把"被保留"的 dim_pct 项按 (table_code, metric, index) 索引（带原始完整信息）
  const existingDimPctMap = new Map<string, IColumnSortItem>();
  for (const item of filtered) {
    if (!isDimPctVirtualColumn(item)) continue;
    const tableCode = item.table_code || 't0';
    const parsed = parseDimPctColumn(item.column);
    if (!parsed) continue;
    const key = `${tableCode}::${parsed.metric}::${parsed.index}`;
    existingDimPctMap.set(key, item);
  }

  for (const item of filtered) {
    // dim_pct 虚拟列延后到对应源 metric 之后统一插入
    if (isDimPctVirtualColumn(item)) {
      continue;
    }
    // 对照组 / 对比时间 / 维度 / 列占比 % 等非源 metric 列：保持原序写入
    if (
      isCompareUserFilterColumn(item.column) ||
      item.is_compare_date ||
      isColumnSortDimension(item) ||
      isPercentVirtualColumn(item)
    ) {
      next.push(item);
      continue;
    }
    const metricName = item.column || '';
    if (!metricName) {
      next.push(item);
      continue;
    }
    const tableCode = item.table_code || 't0';
    next.push(item);

    // 该源指标有没有 dim_pct 配置？
    const groupKey = `${tableCode}::${metricName}`;
    const sub = groupByMetric.get(groupKey);
    if (!sub || sub.length === 0) continue;

    // 准备按 metrics[] 子序列顺序插入的 dim_pct 项
    const outForThis = generatedByMetric.get(groupKey) || [];
    if (outForThis.length === 0 && sub.length > 0) {
      sub.forEach((m, i) => {
        const idx = i + 1; // 1-based
        const key = `${tableCode}::${metricName}::${idx}`;
        const existing = existingDimPctMap.get(key);
        if (existing) {
          outForThis.push({ item: existing, key });
        } else {
          // 新生成：表头使用配置 label，origin_name 用 `dim_pct_{metric}` / `_2` / `_3`
          const dimPctCol = dimPctColumnNameAt(metricName, idx);
          const displayCn = m.label?.trim() || item.column_display_cn || metricName;
          const displayEn = m.label?.trim() || item.column_display_en || displayCn;
          outForThis.push({
            item: {
              ...item,
              column: dimPctCol,
              column_display_cn: displayCn,
              column_display_en: displayEn,
              table_id: item.table_id,
              table_code: tableCode,
              builder_type: item.builder_type,
            },
            key,
          });
        }
      });
      generatedByMetric.set(groupKey, outForThis);
    }

    // 推入
    for (const g of outForThis) {
      next.push(g.item);
    }
  }

  // 3) 尾部兜底：处理主 metric 列已被 column_sort 删除但 dim_pct 列仍需要保留的边界情况
  //    （极少见：用户从 column_sort 删了主 metric 但保留 dim_pct）
  //    本实现遵循「主 metric 在 → dim_pct 在」原则：若主 metric 已不在 filtered 中，
  //    对应 dim_pct 已经在步骤 1 中被 drop 掉。这里不再补回，避免脏数据。

  return next;
}
